import { type Log, erc20Abi, formatUnits, parseEventLogs, zeroAddress } from 'viem'

import { hyperEVM } from '@/config/chain/constants'
import { track, classifyError } from '@/features/analytics'
import { LifiFetchError, fetchLifiQuote, lifiAddressOverride } from '@/domain/lifi/fetchLifiQuote'
import { BaseUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { LifiQuoteResponse } from '@/features/swap/logic/types'
import { toBigInt } from '@/utils/bigNumber'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'
import { captureError } from '../../../../instrumentation-client'
import { GasLimitExceededDetails, SMALL_BLOCK_GAS_LIMIT } from './constants'
import { GetPriceError, GetPriceResult, SwapError, WalletConfig } from './types'

const EXECUTION_QUOTE_MIN_OUTPUT_BUFFER_BPS = 10n
const EXECUTION_QUOTE_DRIFT_RETRIES = 3
const EXECUTION_QUOTE_DRIFT_BASE_DELAY_MS = 500
const lifiGenericSwapCompletedAbi = [
  {
    type: 'event',
    name: 'LiFiGenericSwapCompleted',
    inputs: [
      { name: 'transactionId', type: 'bytes32', indexed: true },
      { name: 'integrator', type: 'string', indexed: false },
      { name: 'referrer', type: 'string', indexed: false },
      { name: 'receiver', type: 'address', indexed: false },
      { name: 'fromAssetId', type: 'address', indexed: false },
      { name: 'toAssetId', type: 'address', indexed: false },
      { name: 'fromAmount', type: 'uint256', indexed: false },
      { name: 'toAmount', type: 'uint256', indexed: false },
    ],
  },
] as const

async function fetchWithRetry<T>(
  fn: () => Promise<T>,
  { retries, baseDelay }: { retries: number; baseDelay: number },
): Promise<T> {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      // Fail immediately on server errors (5xx)
      if (error instanceof LifiFetchError && error.status >= 500) throw error
      if (attempt === retries - 1) throw error
      const delay = baseDelay * 2 ** attempt
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }
  throw new Error('fetchWithRetry: unreachable')
}

interface SwapProps {
  inputToken: Token
  outputToken: Token
  inputAmount: BaseUnitNumber
  priceData?: GetPriceResult | GetPriceError | null
  walletConfig: WalletConfig
  chainId: number
  nativeAssetSymbol: TokenSymbol
  /** Slippage tolerance in basis points (e.g., 50 = 0.5%) */
  slippage: number
  /** Whether the user is currently using big blocks (defaults to false) */
  isUsingBigBlocks?: boolean
  setSwapState: (state: string) => void
  setSwapError: (error: boolean, message?: string) => void
  setQuoteError: (error: boolean, message?: string) => void
  onSuccess: ({ outputAmount }: { outputAmount?: string }) => void
  /** Called when estimated gas exceeds small block limit */
  onGasLimitExceeded?: (details: GasLimitExceededDetails) => void
}

class ExecutionQuoteDriftError extends SwapError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, details)
    this.name = 'ExecutionQuoteDriftError'
  }
}

/**
 * Checks if a token address represents a native token
 */
function isNativeToken(address: string): boolean {
  const nativeAddresses = [zeroAddress.toLowerCase(), '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee']
  return nativeAddresses.includes(address.toLowerCase())
}

export async function swap({
  inputToken,
  outputToken,
  inputAmount,
  priceData,
  walletConfig,
  nativeAssetSymbol,
  slippage,
  isUsingBigBlocks,
  setSwapState,
  setSwapError,
  setQuoteError,
  onSuccess,
  onGasLimitExceeded,
}: SwapProps): Promise<void> {
  setSwapError(false)
  setQuoteError(false)
  setSwapState('Initializing...')

  const { account, publicClient, walletClient } = walletConfig

  if (!account || !publicClient || !walletClient) {
    console.warn('Wallet not available or not an Ethereum wallet')
    setSwapState('')
    return
  }

  // We just need priceData to exist to confirm a route exists
  if (!priceData || !('result' in priceData)) {
    console.warn('Price data not available')
    setSwapState('')
    return
  }

  const startTime = Date.now()
  let currentStep: 'approval' | 'sign' | 'confirmation' = 'sign'
  let inputAmountStr: string | undefined
  let outputAmountStr: string | undefined

  try {
    setSwapState('Getting fresh quote...')

    const requestedInputAmount = toBigInt(inputAmount)
    let lifiQuote!: LifiQuoteResponse

    for (let attempt = 0; attempt < EXECUTION_QUOTE_DRIFT_RETRIES; attempt++) {
      if (attempt > 0) {
        setSwapState(`Refreshing quote (${attempt + 1}/${EXECUTION_QUOTE_DRIFT_RETRIES})...`)
      }

      // Fetch a FRESH quote right before execution with retry logic
      lifiQuote = await fetchWithRetry(
        () =>
          fetchLifiQuote({
            fromToken: lifiAddressOverride(inputToken.address),
            toToken: lifiAddressOverride(outputToken.address),
            fromAmount: requestedInputAmount.toString(),
            fromAddress: account,
            slippageBps: slippage ?? 50,
          }),
        { retries: 3, baseDelay: 1000 },
      )

      const executionInputAmount = BigInt(lifiQuote.action.fromAmount)
      const executionMinOutputAmount = BigInt(lifiQuote.estimate.toAmountMin)

      try {
        assertExecutionQuoteIsWithinAcceptedRange({
          priceQuote: priceData,
          executionInputAmount,
          executionMinOutputAmount,
          requestedInputAmount,
        })
        break
      } catch (error) {
        if (!(error instanceof ExecutionQuoteDriftError) || attempt === EXECUTION_QUOTE_DRIFT_RETRIES - 1) {
          throw error
        }

        const delay = EXECUTION_QUOTE_DRIFT_BASE_DELAY_MS * 2 ** attempt
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }

    const { transactionRequest, estimate } = lifiQuote
    const approvalAddress = estimate.approvalAddress
    const fromAmount = BigInt(lifiQuote.action.fromAmount)
    const isInputNative = inputToken.symbol === nativeAssetSymbol || isNativeToken(inputToken.address)
    const isOutputNative = outputToken.symbol === nativeAssetSymbol || isNativeToken(outputToken.address)

    if (BigInt(estimate.toAmount ?? '0') <= 0n) {
      throw new SwapError('Quote returned zero output amount')
    }

    inputAmountStr = formatUnits(fromAmount, inputToken.decimals)
    outputAmountStr = formatUnits(BigInt(estimate.toAmount), outputToken.decimals)
    const requiresApproval = !isInputNative && !!approvalAddress

    track('swap_submitted', {
      input_token: inputToken.symbol,
      output_token: outputToken.symbol,
      input_amount: inputAmountStr,
      output_amount: outputAmountStr,
      slippage_setting: slippage / 100,
      requires_approval: requiresApproval,
    })

    // Check if token approval is needed (skip for native tokens)
    if (!isInputNative && approvalAddress) {
      const allowance = await publicClient.readContract({
        address: inputToken.address,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [account, approvalAddress as `0x${string}`],
      })

      if (allowance < fromAmount) {
        currentStep = 'approval'
        setSwapState('Approving...')

        track('swap_approval_requested', {
          input_token: inputToken.symbol,
          input_amount: inputAmountStr,
        })

        const approveTx = await walletClient.writeContract({
          address: inputToken.address,
          abi: erc20Abi,
          functionName: 'approve',
          args: [approvalAddress as `0x${string}`, fromAmount],
          chain: hyperEVM,
          account,
        })
        await waitForTxReceipt(publicClient, approveTx)

        track('swap_approval_completed', {
          input_token: inputToken.symbol,
          input_amount: inputAmountStr,
          tx_hash: approveTx,
        })

        currentStep = 'sign'
      }
    }

    setSwapState('Estimating gas...')

    // Estimate gas to check against block limits before sending
    const txParams = {
      to: transactionRequest.to as `0x${string}`,
      data: transactionRequest.data as `0x${string}`,
      account,
      value: transactionRequest.value ? BigInt(transactionRequest.value) : 0n,
      chain: hyperEVM,
    }
    const nativeBalanceBefore =
      isOutputNative && !isInputNative ? await publicClient.getBalance({ address: account }) : undefined

    let estimatedGas: bigint
    try {
      estimatedGas = await publicClient.estimateGas(txParams)
    } catch (simError) {
      console.error('Gas estimation failed:', simError)
      const revertReason =
        simError instanceof Error
          ? ('shortMessage' in simError && typeof simError.shortMessage === 'string'
              ? simError.shortMessage
              : simError.message)
          : String(simError)
      throw new SwapError(`Simulation failed: ${revertReason}`, {
        simulationError: revertReason,
      })
    }

    if (!isUsingBigBlocks && estimatedGas > SMALL_BLOCK_GAS_LIMIT) {
      const gasLimitExceededDetails = {
        estimatedGas,
        limit: SMALL_BLOCK_GAS_LIMIT,
        computationUnits: Number(estimatedGas),
      }

      if (onGasLimitExceeded) {
        onGasLimitExceeded(gasLimitExceededDetails)
      } else {
        throw new SwapError('Gas limit exceeded small block limit', {
          gasLimitExceeded: gasLimitExceededDetails,
        })
      }

      return
    }

    setSwapState('Awaiting confirmation...')

    currentStep = 'sign'
    const hash = await walletClient.sendTransaction(txParams)

    track('swap_signed', {
      input_token: inputToken.symbol,
      output_token: outputToken.symbol,
      // biome-ignore lint/style/noNonNullAssertion: assigned above before this point
      input_amount: inputAmountStr!,
      // biome-ignore lint/style/noNonNullAssertion: assigned above before this point
      output_amount: outputAmountStr!,
      tx_hash: hash,
    })

    setSwapState('Sending transaction...')

    currentStep = 'confirmation'
    // Big blocks take ~1 minute to confirm, so we need longer timeouts
    const tx = await waitForTxReceipt(publicClient, hash, {
      retryCount: isUsingBigBlocks ? 40 : 5,
      retryDelay: isUsingBigBlocks ? 3000 : 2000,
    })

    if (tx.status !== 'success') {
      console.error('Transaction failed', tx)
      throw new SwapError('Transaction failed', { receipt: tx })
    }

    const actualOutputAmount = await parseActualOutputAmount({
      tx,
      account,
      outputToken,
      isInputNative,
      isOutputNative,
      nativeBalanceBefore,
      estimatedToAmount: estimate.toAmount,
      txValue: txParams.value,
      publicClient,
    })

    track('swap_completed', {
      input_token: inputToken.symbol,
      output_token: outputToken.symbol,
      // biome-ignore lint/style/noNonNullAssertion: assigned above before this point
      input_amount: inputAmountStr!,
      // biome-ignore lint/style/noNonNullAssertion: assigned above before this point
      output_amount: outputAmountStr!,
      tx_hash: hash,
      duration_ms: Date.now() - startTime,
    })

    onSuccess({ outputAmount: actualOutputAmount })
  } catch (error) {
    if (classifyError(error) === 'user_rejected') return

    if (error instanceof Error) {
      captureError(error, error instanceof SwapError ? error.details : undefined)
    }

    console.error('In swap: ', error)

    const errorMessage = error instanceof Error ? error.message : 'Transaction failed'

    // Only fire swap_failed if we have inputAmountStr (i.e. quote was fetched)
    if (typeof inputAmountStr !== 'undefined') {
      track('swap_failed', {
        input_token: inputToken.symbol,
        output_token: outputToken.symbol,
        input_amount: inputAmountStr,
        error_type: classifyError(error),
        error_message: errorMessage,
        failed_at: currentStep,
      })
    }

    if (error instanceof LifiFetchError) {
      setQuoteError(true, errorMessage)
    } else {
      setSwapError(true, errorMessage)
    }
  } finally {
    setSwapState('')
  }
}

async function parseActualOutputAmount({
  tx,
  account,
  outputToken,
  isInputNative,
  isOutputNative,
  nativeBalanceBefore,
  estimatedToAmount,
  txValue,
  publicClient,
}: {
  tx: { logs: Log[]; blockNumber: bigint; gasUsed: bigint; effectiveGasPrice: bigint }
  account: `0x${string}`
  outputToken: Token
  isInputNative: boolean
  isOutputNative: boolean
  nativeBalanceBefore: bigint | undefined
  estimatedToAmount: string
  txValue: bigint
  publicClient: { getBalance: (args: { address: `0x${string}`; blockNumber?: bigint }) => Promise<bigint> }
}): Promise<string | undefined> {
  const lifiSwapCompletedLogs = parseEventLogs({
    abi: lifiGenericSwapCompletedAbi,
    logs: tx.logs,
    eventName: 'LiFiGenericSwapCompleted',
    strict: false,
  })
  const lifiSwapCompletedLog = lifiSwapCompletedLogs
    .filter((log) => log.args.receiver?.toLowerCase() === account.toLowerCase())
    .pop()

  if (lifiSwapCompletedLog?.args.toAmount !== undefined) {
    return lifiSwapCompletedLog.args.toAmount.toString()
  }

  if (isOutputNative) {
    if (isInputNative || txValue > 0n) {
      return estimatedToAmount
    }
    const nativeBalanceAfter = await publicClient.getBalance({ address: account, blockNumber: tx.blockNumber })
    const gasSpent = tx.gasUsed * tx.effectiveGasPrice
    return (nativeBalanceAfter - nativeBalanceBefore! + gasSpent).toString()
  }

  const transferLogs = parseEventLogs({
    abi: erc20Abi,
    logs: tx.logs,
    eventName: 'Transfer',
  })
  const outputTransfer = transferLogs
    .filter(
      (log) =>
        log.args.to?.toLowerCase() === account.toLowerCase() &&
        log.address.toLowerCase() === outputToken.address.toLowerCase(),
    )
    .pop()

  return outputTransfer?.args.value?.toString() ?? estimatedToAmount
}

function assertExecutionQuoteIsWithinAcceptedRange({
  priceQuote,
  executionInputAmount,
  executionMinOutputAmount,
  requestedInputAmount,
}: {
  priceQuote: GetPriceResult
  executionInputAmount: bigint
  executionMinOutputAmount: bigint
  requestedInputAmount: bigint
}) {
  const displayedMinOutputAmount = BigInt(priceQuote.result.minOutputAmount)
  const bufferedDisplayedMinOutputAmount =
    (displayedMinOutputAmount * (10_000n - EXECUTION_QUOTE_MIN_OUTPUT_BUFFER_BPS)) / 10_000n

  if (executionInputAmount !== requestedInputAmount) {
    throw new SwapError('Execution quote input amount changed before submission', {
      requestedInputAmount: requestedInputAmount.toString(),
      executionInputAmount: executionInputAmount.toString(),
    })
  }

  if (executionMinOutputAmount < bufferedDisplayedMinOutputAmount) {
    throw new ExecutionQuoteDriftError('Price moved too much before submission. Please try again.', {
      displayedOutputAmount: priceQuote.result.outputAmount,
      displayedMinOutputAmount: priceQuote.result.minOutputAmount,
      executionMinOutputAmount: executionMinOutputAmount.toString(),
      bufferedDisplayedMinOutputAmount: bufferedDisplayedMinOutputAmount.toString(),
      bufferBps: EXECUTION_QUOTE_MIN_OUTPUT_BUFFER_BPS.toString(),
    })
  }
}
